import { Component, Input, inject } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { MassAddTranslationConfig } from '../../models/mass-add-translation-config';

interface PreviewLanguageEntry {
  lang: string;
  newValue: string;
  oldValue?: string;
  isNew: boolean;
  isConflict: boolean;
}

interface PreviewRow {
  key: string;
  keyFormatWarning: boolean;
  languages: PreviewLanguageEntry[];
  errors: string[];
}

@Component({
  selector: 'lib-mass-add-translation-modal',
  standalone: false,
  templateUrl: './mass-add-translation-modal.component.html',
  styleUrl: './mass-add-translation-modal.component.scss'
})
export class MassAddTranslationModalComponent {
  @Input() allLanguages: string[] = [];
  @Input() config: MassAddTranslationConfig;

  public activeModal = inject(NgbActiveModal);

  readonly exampleText = `{
  "PUNCHOUT_CONFIG": { "en": "Punchout Configuration", "se": "Punchoutkonfiguration" },
  "PUNCHOUT_HOOK_URL": { "en": "Hook URL" }
}`;

  view: 'input' | 'preview' = 'input';
  rawInput = '';
  globalParseError = false;
  previewRows: PreviewRow[] = [];

  get hasValidEntries(): boolean {
    return this.previewRows.some(row => row.languages.length > 0);
  }

  preview(): void {
    this.globalParseError = false;
    this.previewRows = [];

    const parsed = this.parseLeniently(this.rawInput);
    if (!parsed) {
      this.globalParseError = true;
      return;
    }

    const rows: PreviewRow[] = Object.keys(parsed).map(key => this.buildPreviewRow(key, parsed[key]));

    const distinctLangs = Array.from(new Set(rows.flatMap(row => row.languages.map(entry => entry.lang))));

    if (distinctLangs.length === 0) {
      this.previewRows = rows;
      this.view = 'preview';
      return;
    }

    this.config.getTranslationsForLanguages(distinctLangs).subscribe(currentByLang => {
      rows.forEach(row => {
        row.languages.forEach(entry => {
          const currentValue = currentByLang[entry.lang]?.[row.key];
          if (currentValue !== undefined) {
            entry.oldValue = currentValue;
            entry.isConflict = currentValue !== entry.newValue;
          } else {
            entry.isNew = true;
          }
        });
      });
      this.previewRows = rows;
      this.view = 'preview';
    });
  }

  confirmImport(): void {
    const entries = this.previewRows.flatMap(row =>
      row.languages.map(entry => ({ key: row.key, lang: entry.lang, value: entry.newValue }))
    );
    this.config.addTranslations(entries);
    this.activeModal.close(true);
  }

  backToInput(): void {
    this.view = 'input';
  }

  cancel(): void {
    this.activeModal.dismiss();
  }

  private buildPreviewRow(key: string, value: any): PreviewRow {
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return {
        key,
        keyFormatWarning: false,
        languages: [],
        errors: [`"${key}" must map to an object of language codes, e.g. { "en": "..." } - skipped`]
      };
    }

    const keyFormatWarning = !/^[A-Z0-9_]+$/.test(key);
    const languages: PreviewLanguageEntry[] = [];
    const errors: string[] = [];

    Object.keys(value).forEach(lang => {
      const langValue = value[lang];
      if (typeof langValue !== 'string') {
        errors.push(`"${key}.${lang}" must be a plain string - skipped`);
        return;
      }
      if (!this.allLanguages.includes(lang)) {
        errors.push(`Unrecognized language "${lang}" for key "${key}" - skipped`);
        return;
      }
      languages.push({ lang, newValue: langValue, isNew: false, isConflict: false });
    });

    if (languages.length === 0 && errors.length === 0) {
      errors.push(`"${key}" has no language values - skipped`);
    }

    return { key, keyFormatWarning, languages, errors };
  }

  private parseLeniently(raw: string): any {
    const trimmed = (raw || '').trim();
    if (!trimmed) {
      return null;
    }
    const withBraces = trimmed.startsWith('{') ? trimmed : `{${trimmed}}`;
    const candidates = [trimmed, withBraces, withBraces.replace(/,\s*}\s*$/, '}')];

    for (const candidate of candidates) {
      try {
        const result = JSON.parse(candidate);
        if (typeof result === 'object' && result !== null && !Array.isArray(result)) {
          return result;
        }
      } catch {
        continue;
      }
    }
    return null;
  }
}
